import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FeatureComparison = ({ plans }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    'Filing & Compliance': true,
    'Automation & AI': false,
    'Scalability & Integrations': false,
    'Support & Service': false
  });

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev?.[category]
    }));
  };

  const planMap = plans?.reduce((acc, plan) => {
    if (!plan?.id) return acc;
    acc[plan?.id] = plan?.name;
    return acc;
  }, {}) || {};

  const planOrder = ['starter', 'pro', 'pro-plus', 'enterprise'];
  const planHeaders = planOrder?.map((key) => ({
    key,
    label: planMap?.[key] || key.replace('-', ' ').replace(/\b\w/g, (char) => char.toUpperCase())
  }));

  const featureCategories = {
    'Filing & Compliance': [
      {
        name: 'MTD ITSA filing',
        description: 'HMRC-ready MTD ITSA workflows',
        values: {
          starter: 'Included',
          pro: 'Included',
          'pro-plus': 'Included',
          enterprise: 'Included'
        }
      },
      {
        name: 'Company Tax (CT600) filing',
        description: 'Automated CT600 preparation and submission',
        values: {
          starter: false,
          pro: 'Included',
          'pro-plus': 'Included',
          enterprise: 'Included'
        }
      },
      {
        name: 'VAT filing & workflows',
        description: 'Digital VAT submission and reconciliation',
        values: {
          starter: false,
          pro: 'Included',
          'pro-plus': 'Included',
          enterprise: 'Included'
        }
      },
      {
        name: 'HMRC integration',
        description: 'Direct filing and status tracking',
        values: {
          starter: 'Included',
          pro: 'Included',
          'pro-plus': 'Included',
          enterprise: 'Included'
        }
      }
    ],
    'Automation & AI': [
      {
        name: 'Smart expense categorisation',
        description: 'AI categorisation that improves with feedback',
        values: {
          starter: 'Smart categorisation',
          pro: 'Advanced + custom rules',
          'pro-plus': 'Advanced + auto reconciliation',
          enterprise: 'Firm-wide automation workflows'
        }
      },
      {
        name: 'Gemini Pro integration',
        description: 'Real-time financial research assistance',
        values: {
          starter: false,
          pro: 'Included',
          'pro-plus': 'Included',
          enterprise: 'Included'
        }
      },
      {
        name: 'Scheduled AI reports & alerts',
        description: 'Automated reporting delivered to your inbox',
        values: {
          starter: false,
          pro: 'Included',
          'pro-plus': 'Included',
          enterprise: 'Included + custom alerts'
        }
      }
    ],
    'Scalability & Integrations': [
      {
        name: 'Invoice & PDF exports',
        description: 'Automated invoice creation and delivery',
        values: {
          starter: 'Up to 10/month',
          pro: 'Unlimited',
          'pro-plus': 'Unlimited + auto delivery',
          enterprise: 'Unlimited + white-label delivery'
        }
      },
      {
        name: 'Multi-business management',
        description: 'Handle multiple entities from one workspace',
        values: {
          starter: 'Single business',
          pro: 'Up to 3 entities',
          'pro-plus': 'Unlimited entities',
          enterprise: 'Consolidated multi-firm'
        }
      },
      {
        name: 'Live bank sync (Open Banking)',
        description: 'Secure Open Banking connections',
        values: {
          starter: false,
          pro: false,
          'pro-plus': 'Included (Early access)',
          enterprise: 'Included + SLAs'
        }
      }
    ],
    'Support & Service': [
      {
        name: 'Support level',
        description: 'Channels and support coverage',
        values: {
          starter: 'Email & in-app + developer support',
          pro: 'Email & in-app',
          'pro-plus': 'Email & in-app + higher priority developer support',
          enterprise: 'Email & in-app • highest priority developer support'
        }
      }
    ]
  };

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Icon name="Check" size={16} className="text-accent" />;
    }
    if (value === false || value === null || value === undefined) {
      return <Icon name="Minus" size={16} className="text-muted-foreground" />;
    }

    const textValue = String(value);
    const lowerValue = textValue?.toLowerCase?.() || '';

    if (lowerValue.includes('coming soon')) {
      return <span className="text-sm text-foreground font-medium">Included</span>;
    }

    if (lowerValue.includes('waitlist') || lowerValue.includes('invitation')) {
      return (
        <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full bg-secondary/10 text-secondary">
          {textValue}
        </span>
      );
    }

    return <span className="text-sm text-foreground font-medium">{textValue}</span>;
  };

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="p-8 border-b border-border">
        <h3 className="text-2xl font-bold text-foreground mb-2">
          Detailed Feature Comparison
        </h3>
        <p className="text-muted-foreground">
          Compare core capabilities across Neevii’s subscription tiers.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <th className="text-left p-6 font-semibold text-foreground min-w-[260px]">
                Features
              </th>
              {planHeaders?.map((plan) => (
                <th key={plan?.key} className="text-center p-6 font-semibold text-foreground min-w-[150px]">
                  {plan?.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(featureCategories)?.map(([category, items]) => (
              <React.Fragment key={category}>
                <tr className="border-t border-border">
                  <td colSpan={planHeaders?.length + 1} className="p-0">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-6 bg-muted/10 hover:bg-muted/20 transition-colors"
                    >
                      <span className="font-semibold text-foreground">{category}</span>
                      <Icon
                        name={expandedCategories?.[category] ? 'ChevronUp' : 'ChevronDown'}
                        size={20}
                        className="text-muted-foreground"
                      />
                    </button>
                  </td>
                </tr>
                {expandedCategories?.[category] && items?.map((item, index) => (
                  <tr key={`${category}-${item?.name}-${index}`} className="border-t border-border hover:bg-muted/5">
                    <td className="p-6">
                      <div>
                        <div className="font-medium text-foreground mb-1">
                          {item?.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {item?.description}
                        </div>
                      </div>
                    </td>
                    {planHeaders?.map((plan) => (
                      <td key={plan?.key} className="p-6 text-center">
                        {renderFeatureValue(item?.values?.[plan?.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeatureComparison;